import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PortalHttpClient } from '@grid-ui/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { PollStatusEnum } from '../../models';
import {
  continueOngoingPoll,
  continuePollingSuccess,
  pollAction,
  pollingComplete,
  pollInitialCallError,
  pollStatusCallError,
  startPolling,
  updateExtractId,
  updatePollStatus,
} from '../actions/polling.actions';
import * as fromPolling from '../reducers';

@Injectable()
export class PollingEffects {
  public startPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startPolling),
      map((action) => action),
      switchMap(({ poll }) => {
        const headers: any = {};
        if (poll.authToken) {
          headers['Authorization'] = poll.authToken;
        }
        return this.http
          .post(poll.apiConfig, {
            body: poll.requestBody,
            httpOptions: {
              headers,
            },
          })
          .pipe(
            mergeMap((result: any) =>
              of(
                pollAction({
                  poll: {
                    ...poll,
                    serverPollingSessionId: result.id,
                    status: PollStatusEnum.Active,
                  },
                }),
              ),
            ),
            catchError(() =>
              // TODO: When merging this with GRID polling, watch out the
              // specific cases for GRID w.r.t. response codes and request body.
              // let status = error.status === 400 ? PollStatusEnum.FailedDuplicate : PollStatusEnum.Failed;
              //
              // if (poll.requestBody && !poll.requestBody.editions) {
              //   status = PollStatusEnum.ParameterRequired;
              // }

              of(updatePollStatus({ id: poll.id, status: PollStatusEnum.Failed }), pollInitialCallError({ id: poll.id })),
            ),
          );
      }),
    ),
  );

  public continuePolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(pollAction, continueOngoingPoll),
      withLatestFrom(this.store),
      map(([action, state]) => {
        const poll = state.polling.pollList.entities[action.poll.id];
        return {
          poll: action.poll,
          userCancel: poll !== undefined && !!poll.userCancelled,
        };
      }),
      switchMap(({ poll, userCancel }) => {
        if (userCancel) {
          return of(updatePollStatus({ id: poll.id, status: PollStatusEnum.Cancelled }), pollingComplete({ id: poll.id }));
        }
        const headers: any = {};
        if (poll.authToken) {
          headers['Authorization'] = poll.authToken;
        }
        return this.http
          .get<{ status: string }>(poll.apiConfig.pollingEndpoint, {
            pathParams: { id: poll.serverPollingSessionId },
            httpOptions: {
              headers,
            },
          })
          .pipe(
            mergeMap((response: any) => of(continuePollingSuccess({ response, poll }))),
            catchError((error: HttpErrorResponse) =>
              error.status !== 408
                ? of(updatePollStatus({ id: poll.id, status: PollStatusEnum.Failed }), pollStatusCallError({ id: poll.id }))
                : of(updatePollStatus({ id: poll.id, status: PollStatusEnum.TimedOut }), pollStatusCallError({ id: poll.id })),
            ),
          );
      }),
    ),
  );

  public continuePollingSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(continuePollingSuccess),
      mergeMap(({ poll, response }) => {
        const status = this.getResponsePollStatus(response);
        if (status === 'cancelled') {
          return of(updatePollStatus({ id: poll.id, status: PollStatusEnum.Cancelled }), pollingComplete({ id: poll.id }));
        }
        if (status === 'failed') {
          return of(updatePollStatus({ id: poll.id, status: PollStatusEnum.Failed }), pollingComplete({ id: poll.id }));
        }
        if (status === 'complete') {
          return of(
            updateExtractId({
              id: poll.id,
              extractId: response.link ? response.link.id : undefined,
            }),
            updatePollStatus({
              id: poll.id,
              status: PollStatusEnum.Completed,
            }),
            pollingComplete({ id: poll.id }),
          );
        }
        if (status === 'score_in_progress') {
          return of(updatePollStatus({ id: poll.id, status: PollStatusEnum.FailedScoringInProgress }), pollingComplete({ id: poll.id }));
        } else if (poll.pollCount >= poll.apiConfig.pollLimit) {
          return of(updatePollStatus({ id: poll.id, status: PollStatusEnum.TimedOut }), pollingComplete({ id: poll.id }));
        }
        return of(
          pollAction({
            poll: {
              ...poll,
              pollCount: poll.pollCount + 1,
            },
          }),
        ).pipe(delay(poll.apiConfig.pollingInterval));
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly http: PortalHttpClient,
    private readonly store: Store<fromPolling.State>,
  ) {}

  /**
   * The API can return the status as a string (e.g. 'cancelled') or as an object that
   * contains an status code and string.
   */
  private getResponsePollStatus(response: { status: string | { status_string: string } }): string {
    const status = response.status;
    if (status instanceof Object && Object.prototype.hasOwnProperty.call(status, 'status_string')) {
      return status.status_string.toLocaleLowerCase();
    } else if (typeof status === 'string') {
      return status;
    } else {
      console.error('Unrecognized Country Risk Extract poll status: ' + status);
      return '';
    }
  }
}
